import { makeAutoObservable, runInAction } from 'mobx';
import i18n from 'i18n';
import {
  MAX_UPLOADED_FILES_SIZE,
  AFTER_VALIDATE_PAYMENT_METHODS_SORTING_EXAMPLE,
  BEFORE_VALIDATE_PAYMENT_METHODS_SORTING_EXAMPLE,
  PAYMENT_PROVIDERS,
  MANUAL_PROVIDER_PAYMENT_METHODS_SORTING_EXAMPLE,
  BACKEND_MODULE_TYPES,
  PAGINATION_SIZES,
  TEMPLATE_CREATION_STEPS,
  PAYMENT_METHOD,
  TEMPLATE_CREATION_STEPS_FOR_SWIFT,
  TEMPLATE_CREATION_STEPS_FOR_OTHER_PAYMENT_METHODS,
  TEMPLATE_CREATION_STEPS_FOR_CRYPTO_PAYMENT,
  H_AND_H_PROVIDER_PAYMENT_METHODS_SORTING_EXAMPLE,
  DUKASCOPY_PROVIDER_PAYMENT_METHODS_SORTING_EXAMPLE,
  GRAPHEREX_PROVIDER_PAYMENT_METHODS_SORTING_EXAMPLE
} from 'components/common/constants';
import {
  filteringPaymentMethods,
  sortingPaymentMethods,
  moveSelectedMethodToFront,
  mapIbanCredentials
} from 'components/common/PaymentForm/utils';
import { setTimerDurationExpiresAt, updateConfirmationActionType } from 'services/authUtils';
import PreparedTransactionsError from 'services/Error/PreparedTransactionsError';
import { convertDateToServerFilter, debounce, replaceSpaces } from 'services/utils';
import {
  transactionPaymentCheck,
  transactionPaymentCreate,
  uploadDocuments,
  transactionPaymentConfirm,
  resendSecurityCode,
  calculatePaymentCommissionRequest,
  transactionCryptoCheck,
  transactionCryptoCreate,
  transactionCryptoConfirm,
  validateRecipientIbanOrCryptoWalletAddressRequest,
  getPreparedTransactionsList,
  createPreparedTransaction,
  updatePreparedTransactionRequest,
  createPreparedCryptoTransaction,
  updatePreparedCryptoTransaction,
  deletePreparedTransactions,
  submitPreparedTransactions,
  confirmPreparedTransactions,
  calculatePaymentCommissionInBatchRequest,
  getPreparedTransactionDataForEditRequest,
  validateTemplateAccountDetailsRequest,
  validateTemplateBankDetailsRequest,
  validateTemplateRecipientDetailsRequest,
  validateTemplateAdditionalRecipientDetailsRequest,
  validateTemplatePaymentDetailsRequest,
  createTemplateRequest,
  updateTemplateRequest,
  deleteTemplateRequest,
  getTemplatesListRequest,
  validateTemplatePaymentAmountRequest,
  forceChangeConfirmationTypeRequest,
  importCsvTransactions
} from 'services/requestAgent';

class PaymentStore {
  isLoading = false;
  isCommissionLoading = false;
  isIbanCheckLoading = false;
  isFileUploading = false;
  isShowingInternalActionConfirmation = false;
  isShowSwitchConfirmationMethodScreen = false;
  isShowingConfirmationTab = false;
  error = null;
  confirmationPopupError = null;
  temporaryError = null;
  currentAccount = null;
  providerData = {};
  paymentMethod = null;
  availablePaymentMethods = [];

  uploadedFiles = [];
  isInternalIban = null;
  commission = {
    value: '',
    currency: '',
    type: '',
    commissionDetails: null
  };
  transactionForCreate = null;
  preparedTransactionDataForUpdate = null;
  serverTransactionData = null;
  transactionNumber = null;
  isSuccess = false;
  previousTransactionInfo = null;
  isRepeatPayment = false;
  ibanCredentials = null;
  iban = null;
  previousIban = null;
  ibanWarning = null;
  isNonIban = false;
  confirmationId = null;

  preparedTransactionsFilters = {
    search_text: '',
    from_date: null,
    to_date: null,
    currency: null,
    wallet_number: null
  };

  preparedTransactionsPagination = {
    size: PAGINATION_SIZES[0],
    page: 0
  };

  preparedTransactions = [];
  isPreparedTransactionsFetched = false;
  preparedTransactionsTotalElements = null;
  preparedTransactionsTotalPages = null;
  isPreparedTransactionsLoading = false;
  isPreparedTransactionsCreation = false;
  isPreparedTransactionsSuccessfullyCreated = false;
  isPreparedTransactionsSuccessfullyDeleted = false;
  isPreparedTransactionsSuccessfullyConfirmed = false;
  preparedTransactionIdForEdit = null;
  preparedTransactionsError = null;

  isCsvImporting = false;
  csvImportError = null;
  csvImportValidationErrors = null;

  templatesList = [];
  currentTemplateStep = TEMPLATE_CREATION_STEPS.ACCOUNT_DETAILS;
  templateData = {};
  templateSteps = TEMPLATE_CREATION_STEPS_FOR_OTHER_PAYMENT_METHODS;
  isTemplateSuccessfullyCreated = false;
  isTemplateSuccessfullyUpdated = false;
  isTemplateSuccessfullyDeleted = false;
  isTemplateReadyToUse = false;
  isTemplatesListFetched = false;
  isTemplateUpdating = false;
  isTemplateTransaction = false;
  templatesTotalElements = null;
  templatesTotalPages = null;

  templatesFilters = {
    search_text: '',
    currency: null
  };

  templatesPagination = {
    size: PAGINATION_SIZES[0],
    page: 0
  };

  constructor() {
    makeAutoObservable(this);
    this.setPreparedTransactionsFilter = this.setPreparedTransactionsFilter.bind(this);
    this.clearConfirmationPopupError = this.clearConfirmationPopupError.bind(this);
    this.setConfirmationId = this.setConfirmationId.bind(this);
    this.setIsPreparedTransactionsCreation = this.setIsPreparedTransactionsCreation.bind(this);
    this.setIsPreparedTransactionsSuccessfullyCreated = this.setIsPreparedTransactionsSuccessfullyCreated.bind(this);
    this.resetPreparedTransactionData = this.resetPreparedTransactionData.bind(this);
    this.resendPaymentSecurityCode = this.resendPaymentSecurityCode.bind(this);
    this.resetTemplateCreationData = this.resetTemplateCreationData.bind(this);
    this.setTemplatesFilter = this.setTemplatesFilter.bind(this);
    this.setCurrentTemplateStep = this.setCurrentTemplateStep.bind(this);
    this.setTemplateData = this.setTemplateData.bind(this);
    this.setIsTemplateSuccessfullyCreated = this.setIsTemplateSuccessfullyCreated.bind(this);
    this.setIsTemplateSuccessfullyUpdated = this.setIsTemplateSuccessfullyUpdated.bind(this);
    this.setIsTemplateSuccessfullyDeleted = this.setIsTemplateSuccessfullyDeleted.bind(this);
    this.setIsTemplateTransaction = this.setIsTemplateTransaction.bind(this);
    this.setIsShowingInternalActionConfirmation = this.setIsShowingInternalActionConfirmation.bind(this);
    this.setIsShowSwitchConfirmationMethodScreen = this.setIsShowSwitchConfirmationMethodScreen.bind(this);
    this.setEditTemplateId = this.setEditTemplateId.bind(this);
    this.getCurrentStepIndex = this.getCurrentStepIndex.bind(this);
    this.goToNextStep = this.goToNextStep.bind(this);
    this.goToPrevStep = this.goToPrevStep.bind(this);
    this.setIsShowingConfirmationTab = this.setIsShowingConfirmationTab.bind(this);
  }

  resetPaymentStore() {
    this.isLoading = false;
    this.isShowingInternalActionConfirmation = false;
    this.isShowSwitchConfirmationMethodScreen = false;
    this.isShowingConfirmationTab = false;
    this.isCommissionLoading = false;
    this.isFileUploading = false;
    this.error = null;
    this.providerData = {};
    this.paymentMethod = null;
    this.availablePaymentMethods = [];
    this.uploadedFiles = [];
    this.commission = {
      value: '',
      currency: '',
      type: '',
      commissionDetails: null
    };
    this.transactionForCreate = null;
    this.preparedTransactionDataForUpdate = null;
    this.serverTransactionData = null;
    this.transactionNumber = null;
    this.isSuccess = false;
    this.previousTransactionInfo = null;
    this.isRepeatPayment = false;
    this.confirmationPopupError = null;
    this.currentAccount = null;
    this.ibanCredentials = null;
    this.iban = null;
    this.previousIban = null;
    this.ibanWarning = null;
    this.isNonIban = false;
    this.confirmationId = null;

    this.preparedTransactionsFilters = {
      search_text: '',
      from_date: null,
      to_date: null,
      currency: null,
      wallet_number: null
    };

    this.preparedTransactionsPagination = {
      size: PAGINATION_SIZES[0],
      page: 0
    };

    this.preparedTransactions = [];
    this.isPreparedTransactionsFetched = false;
    this.preparedTransactionsTotalElements = null;
    this.preparedTransactionsTotalPages = null;
    this.isPreparedTransactionsLoading = false;
    this.isPreparedTransactionsCreation = false;
    this.isPreparedTransactionsSuccessfullyCreated = false;
    this.isPreparedTransactionsSuccessfullyDeleted = false;
    this.isPreparedTransactionsSuccessfullyConfirmed = false;
    this.preparedTransactionIdForEdit = null;
    this.preparedTransactionsError = null;

    this.templatesList = [];
    this.currentTemplateStep = TEMPLATE_CREATION_STEPS.ACCOUNT_DETAILS;
    this.templateData = {};
    this.templateSteps = TEMPLATE_CREATION_STEPS_FOR_OTHER_PAYMENT_METHODS;
    this.isTemplateSuccessfullyCreated = false;
    this.isTemplateSuccessfullyUpdated = false;
    this.isTemplateSuccessfullyDeleted = false;
    this.isTemplatesListFetched = false;
    this.isTemplateUpdating = false;
    this.isTemplateReadyToUse = false;
    this.isTemplateTransaction = false;
    this.editTemplateId = null;
    this.templatesTotalElements = null;
    this.templatesTotalPages = null;

    this.templatesFilters = {
      search_text: '',
      currency: null
    };

    this.templatesPagination = {
      size: PAGINATION_SIZES[0],
      page: 0
    };
  }

  setIsLoading(status) {
    this.isLoading = status;
    this.error = null;
  }

  setIsShowingInternalActionConfirmation(status) {
    this.isShowingInternalActionConfirmation = status;
  }

  setIsShowSwitchConfirmationMethodScreen(status) {
    this.isShowSwitchConfirmationMethodScreen = status;
  }

  setIsShowingConfirmationTab(status) {
    this.isShowingConfirmationTab = status;
  }

  setError(error) {
    this.error = error;
  }

  setTemporaryError(error) {
    this.temporaryError = error;
    if (error) {
      // Clear temporary error after 5 seconds
      setTimeout(() => {
        runInAction(() => {
          if (this.temporaryError?.code === error?.code) {
            this.temporaryError = null;
          }
        });
      }, 5000);
    }
  }

  clearConfirmationPopupError() {
    this.confirmationPopupError = null;
  }

  clearIbanCredentials() {
    this.ibanCredentials = null;
    this.iban = null;
    this.previousIban = null;
    this.ibanWarning = null;
  }

  setIsCommissionLoading(status) {
    this.isCommissionLoading = status;
    this.error = null;
  }

  setIsFileUploading(status) {
    this.isFileUploading = status;
    this.error = null;
  }

  setIsIbanCheckLoading(status) {
    this.isIbanCheckLoading = status;
    this.error = null;
    this.ibanWarning = null;
  }

  setIbanWarning(warning) {
    this.ibanWarning = warning;
  }

  setSelectedAccount(account) {
    this.currentAccount = account;
  }

  setPreviousTransactionInfo(transaction, isForApprovalTransactionEdit = false, isCryptoCurrency = false) {
    this.previousTransactionInfo = {
      ...transaction,
      amount: transaction?.amountDetails?.amount || transaction?.amount
    };
    this.iban = transaction?.counterparty?.iban;

    if (isForApprovalTransactionEdit && transaction?.documents) {
      this.uploadedFiles = [
        ...transaction.documents.map((file) => ({
          name: file?.name,
          id: file?.id,
          transactionNumber: file?.transactionNumber
        }))
      ];
    }
  }

  setIsRepeatPaymentStatus(status) {
    this.isRepeatPayment = status;
  }

  setProviderData(providerData) {
    this.providerData = providerData;
  }

  setPaymentMethod(paymentMethod) {
    this.paymentMethod = paymentMethod;
  }

  setAvailablePaymentMethods(methods) {
    const sortedMethods = sortingPaymentMethods(
      filteringPaymentMethods(methods),
      this.currentAccount?.currency,
      this.currentAccount?.transfer_provider === PAYMENT_PROVIDERS.MANUAL
        ? MANUAL_PROVIDER_PAYMENT_METHODS_SORTING_EXAMPLE
        : this.currentAccount?.transfer_provider === PAYMENT_PROVIDERS.GRAPHEREX
          ? GRAPHEREX_PROVIDER_PAYMENT_METHODS_SORTING_EXAMPLE
          : this.currentAccount?.transfer_provider === PAYMENT_PROVIDERS.DUKASCOPY
            ? DUKASCOPY_PROVIDER_PAYMENT_METHODS_SORTING_EXAMPLE
            : this.currentAccount?.transfer_provider === PAYMENT_PROVIDERS.H_AND_H ||
                this.currentAccount?.transfer_provider === PAYMENT_PROVIDERS.CONVERA ||
                this.currentAccount?.transfer_provider === PAYMENT_PROVIDERS.CONVERA_MERCHANT ||
                this.currentAccount?.transfer_provider === PAYMENT_PROVIDERS.CONVERA_API
              ? H_AND_H_PROVIDER_PAYMENT_METHODS_SORTING_EXAMPLE
              : BEFORE_VALIDATE_PAYMENT_METHODS_SORTING_EXAMPLE,
      this.isNonIban,
      this.currentAccount?.transfer_provider
    );

    runInAction(() => {
      this.availablePaymentMethods = sortedMethods;
    });
  }

  setIban(iban) {
    this.iban = iban;
  }

  setIsNonIban(status) {
    this.isNonIban = status;
  }

  resetPaymentMethodInfo() {
    this.setAvailablePaymentMethods(
      filteringPaymentMethods(this.currentAccount?.payment_methods, this.currentAccount?.transfer_provider)
    );
  }

  setAccountInfo(account) {
    this.setSelectedAccount(account);
    this.setAvailablePaymentMethods(
      filteringPaymentMethods(this.currentAccount?.payment_methods, this.currentAccount?.transfer_provider)
    );
    if (this.availablePaymentMethods) {
      this.setPaymentMethod(this.availablePaymentMethods[0]);
    }
    this.setPaymentCommission({
      value: '',
      currency: ''
    });
    this.setError(null);
    this.clearIbanCredentials();
    this.setPreviousTransactionInfo(null);
    this.providerData = {};
  }

  setPaymentCommission(commission) {
    this.commission = commission;
  }

  resetSuccess() {
    this.isSuccess = false;
    this.isShowingConfirmationTab = false;
    this.providerData = {};
  }

  removePaymentFile(fileId) {
    this.uploadedFiles = this.uploadedFiles.filter((file) => file?.id !== fileId);
    // Clear any file upload errors when removing files
    if (this.error?.type === 'attachDoc') {
      this.error = null;
    }
  }

  uploadDocuments(customerNumber) {
    return async ({ target: { files } }) => {
      if (!Object.keys(files).length) {
        return null;
      }
      this.setIsFileUploading(true);
      try {
        const selectedFiles = [...files];
        const Data = new FormData();
        selectedFiles.forEach((file) => {
          if (file.size > MAX_UPLOADED_FILES_SIZE) {
            throw { code: 'REQUEST_HAS_BEEN_TERMINATED' };
          } else {
            Data.append('file', file, file?.name);
          }
        });
        const documentArray = await uploadDocuments(customerNumber, Data);

        runInAction(() => {
          this.isFileUploading = false;
          // Clear any file upload errors on successful upload
          if (this.error?.type === 'attachDoc') {
            this.error = null;
          }
          this.uploadedFiles = [
            ...this.uploadedFiles,
            ...documentArray.map((file, index) => ({
              name: file?.name,
              id: file?.id,
              // Keep reference to original file for preview
              originalFile: selectedFiles[index],
              type: selectedFiles[index]?.type,
              size: selectedFiles[index]?.size
            }))
          ];
        });
      } catch (err) {
        runInAction(() => {
          this.isFileUploading = false;
          this.error = { type: 'attachDoc', ...err };
        });
      }
    };
  }

  async calculatePaymentCommission(customerNumber, data) {
    this.setIsCommissionLoading(true);
    try {
      const { commission, commissionDetails } = await calculatePaymentCommissionRequest(customerNumber, data);

      runInAction(() => {
        this.commission = {
          value: commissionDetails?.amount || commission,
          currency: commissionDetails?.currency || this.currentAccount?.currency,
          commissionDetails
        };
        this.isCommissionLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
        this.isCommissionLoading = false;
      });
    }
  }

  async checkTransfer(customerNumber, data, isForApprovalTransactionEdit) {
    // Skip server check for ForApproval transactions - they should only validate on Save
    if (isForApprovalTransactionEdit) {
      return;
    }

    this.setIsLoading(true);
    try {
      const transactionData = await transactionPaymentCheck(customerNumber, data);

      runInAction(() => {
        if (!this.isRepeatPayment) {
          this.paymentMethod = transactionData.payment_method;
        }

        if (this.isRepeatPayment) {
          this.isRepeatPayment = false;
        }

        this.serverTransactionData = { ...this.serverTransactionData, amountDetails: transactionData?.amountDetails };
        this.error = null;
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        if (err.code === 'WALLET_NOT_FOUND') {
          this.error = { code: err.code, message: i18n.getMessage('error.WALLET_NOT_FOUND') };
        }
        this.error = err;
        this.isLoading = false;
      });
    }
  }

  // New method specifically for ForApproval transaction validation
  async validateForApprovalTransaction({ providerData, model }) {
    // Only validate files for ForApproval transactions
    let uploadedFilesError = null;

    if (this.uploadedFiles.length > 0) {
      this.uploadedFiles.forEach((file) => {
        if (file?.transactionNumber) {
          uploadedFilesError = {
            type: 'attachDoc',
            code: 'ALREADY_LINKED',
            message: i18n.getMessage('attachDoc.error.ALREADY_LINKED')
          };
        }
      });
    }

    // Format data using the model like in validateTransfer
    const data = model({
      providerTableData: providerData,
      data: {
        wallet: this.currentAccount,
        paymentMethod: this.paymentMethod,
        uploadedFiles: this.uploadedFiles.map((file) => file.id),
        ...this.ibanCredentials
      }
    });

    runInAction(() => {
      // Store transaction data without server validation
      this.transactionForCreate = data;
      this.error = uploadedFilesError;
    });

    return !uploadedFilesError;
  }

  // New method specifically for ForApproval crypto transaction validation
  async validateForApprovalCryptoTransaction({ providerData, model }) {
    // Only validate files for ForApproval transactions
    let uploadedFilesError = null;

    if (this.uploadedFiles.length > 0) {
      this.uploadedFiles.forEach((file) => {
        if (file?.transactionNumber) {
          uploadedFilesError = {
            type: 'attachDoc',
            code: 'ALREADY_LINKED',
            message: i18n.getMessage('attachDoc.error.ALREADY_LINKED')
          };
        }
      });
    }

    // Format data using the model like in validateCryptoTransfer
    const data = model({
      providerTableData: providerData,
      data: {
        wallet: this.currentAccount,
        paymentMethod: this.paymentMethod,
        uploadedFiles: this.uploadedFiles.map((file) => file.id),
        ...this.ibanCredentials
      }
    });

    runInAction(() => {
      // Store transaction data without server validation
      this.transactionForCreate = data;
      this.error = uploadedFilesError;
    });

    return !uploadedFilesError;
  }

  async validateIban(customerNumber, senderAccountNumber, iban) {
    if (iban !== this.previousIban) {
      this.setIsIbanCheckLoading(true);
      this.setIban(iban);
      this.ibanCredentials = null;
      try {
        const ibanCredentials = mapIbanCredentials(
          await validateRecipientIbanOrCryptoWalletAddressRequest(customerNumber, {
            senderWalletNumber: senderAccountNumber,
            recipientIban: replaceSpaces(iban)
          })
        );

        const sortedPaymentMethods = sortingPaymentMethods(
          filteringPaymentMethods(ibanCredentials?.payment_methods, this.currentAccount?.transfer_provider),
          this.currentAccount?.currency,
          this.currentAccount?.transfer_provider === PAYMENT_PROVIDERS.MANUAL
            ? MANUAL_PROVIDER_PAYMENT_METHODS_SORTING_EXAMPLE
            : this.currentAccount?.transfer_provider === PAYMENT_PROVIDERS.GRAPHEREX
              ? GRAPHEREX_PROVIDER_PAYMENT_METHODS_SORTING_EXAMPLE
              : this.currentAccount?.transfer_provider === PAYMENT_PROVIDERS.DUKASCOPY
                ? DUKASCOPY_PROVIDER_PAYMENT_METHODS_SORTING_EXAMPLE
                : this.currentAccount?.transfer_provider === PAYMENT_PROVIDERS.H_AND_H ||
                    this.currentAccount?.transfer_provider === PAYMENT_PROVIDERS.CONVERA ||
                    this.currentAccount?.transfer_provider === PAYMENT_PROVIDERS.CONVERA_MERCHANT ||
                    this.currentAccount?.transfer_provider === PAYMENT_PROVIDERS.CONVERA_API
                  ? H_AND_H_PROVIDER_PAYMENT_METHODS_SORTING_EXAMPLE
                  : AFTER_VALIDATE_PAYMENT_METHODS_SORTING_EXAMPLE,
          this.isNonIban,
          this.currentAccount?.transfer_provider
        );

        runInAction(() => {
          this.isInternalIban = true;
          this.ibanCredentials = ibanCredentials;

          if (this.isRepeatPayment) {
            this.availablePaymentMethods = moveSelectedMethodToFront(sortedPaymentMethods, this.paymentMethod);
            this.isRepeatPayment = false;
          } else {
            this.availablePaymentMethods = sortedPaymentMethods;
            this.paymentMethod = this.availablePaymentMethods[0];
          }
          if (ibanCredentials?.payment_methods.length === 0) {
            this.error = { fields: [{ field: 'iban', code: 'NO_PAYMENT_METHODS_AVAILABLE' }] };
          }
        });
      } catch (err) {
        runInAction(() => {
          if (err?.code && err.code === 'IBAN_CHECK_FAILED') {
            // Check if IBAN is structurally valid but bank not found
            if (err?.details?.bankNotFound || err?.message?.includes('bank not found')) {
              // Show warning instead of error
              this.setIbanWarning(i18n.getMessage('transfer.form.iban.warning.bankNotFound'));

              // Set default payment methods (SEPA preferred, SWIFT as fallback)
              const sepaMethod = this.availablePaymentMethods?.find(
                (m) => m === 'SEPA' || m?.toUpperCase?.() === 'SEPA'
              );
              const swiftMethod = this.availablePaymentMethods?.find(
                (m) => m === 'SWIFT' || m?.toUpperCase?.() === 'SWIFT'
              );

              if (sepaMethod) {
                this.paymentMethod = sepaMethod;
              } else if (swiftMethod) {
                this.paymentMethod = swiftMethod;
              } else if (this.availablePaymentMethods?.length > 0) {
                // Fallback to first available method
                this.paymentMethod = this.availablePaymentMethods[0];
              }

              // Set basic IBAN credentials to allow form submission
              this.ibanCredentials = {
                bank_name: 'Unknown Bank',
                payment_methods: this.availablePaymentMethods || ['SEPA', 'SWIFT']
              };

              // Don't set error to allow form submission
            } else {
              // Regular IBAN validation error
              this.error = { fields: [{ field: 'iban', code: 'EXTERNAL_IBAN_VALIDATION_FAILED' }] };
            }
          } else {
            this.error = err;
          }
        });
      } finally {
        runInAction(() => {
          this.previousIban = iban;
          this.isIbanCheckLoading = false;
        });
      }
    }
  }

  async validateTransfer({ customerNumber, providerData, model }) {
    this.setIsLoading(true);
    const data = model({
      providerTableData: providerData,
      data: {
        wallet: this.currentAccount,
        paymentMethod: this.paymentMethod,
        uploadedFiles: this.uploadedFiles.map((file) => file.id),
        ...this.ibanCredentials
      }
    });

    try {
      const transactionData = await transactionPaymentCheck(customerNumber, data);

      runInAction(() => {
        this.isLoading = false;
        if (!this.isPreparedTransactionsCreation) {
          this.isShowingConfirmationTab = true;
        }
        this.paymentMethod = transactionData.payment_method;
        this.transactionForCreate = {
          ...data,
          source_amount: transactionData.sourceAmountDetails?.amount || transactionData.source_amount,
          target_amount: transactionData.targetAmountDetails?.amount || transactionData.target_amount
        };
        this.serverTransactionData = transactionData;
        this.commission = {
          value: transactionData.totalCommissionDetails?.amount || transactionData.total_commissions,
          currency: transactionData.totalCommissionDetails?.currency || transactionData.currency_code,
          type: transactionData.commission_type,
          commissionDetails: transactionData.totalCommissionDetails
        };
      });
    } catch (err) {
      runInAction(() => {
        this.isLoading = false;
        this.error = err;
      });
    }
  }

  createTransfer(customerNumber) {
    return async () => {
      this.setIsLoading(true);
      try {
        const {
          confirmation_id: confirmationId,
          confirmation_type: confirmationType,
          expires_at: expiresAt
        } = await transactionPaymentCreate(customerNumber, {
          ...this.transactionForCreate
        });

        setTimerDurationExpiresAt(expiresAt);
        updateConfirmationActionType(confirmationType);

        runInAction(() => {
          this.isLoading = false;
          this.confirmationId = confirmationId;
          this.isShowingInternalActionConfirmation = true;
        });
      } catch (err) {
        runInAction(() => {
          this.isLoading = false;
          this.confirmationPopupError = err;
        });
      }
    };
  }

  async verifyPaymentSecurityCode(customerNumber, securityCode) {
    try {
      this.setIsLoading(true);
      await transactionPaymentConfirm(customerNumber, {
        code: securityCode,
        confirmation_id: this.confirmationId
      });

      runInAction(() => {
        this.isLoading = false;
        this.transactionNumber = null;
        this.isShowingConfirmationTab = false;
        this.isShowingInternalActionConfirmation = false;
        this.isSuccess = true;
        this.uploadedFiles = [];
        this.previousTransactionInfo = null;
        this.ibanCredentials = null;
        this.iban = null;
        this.previousIban = null;
        this.providerData = {};
      });
    } catch (err) {
      runInAction(() => {
        this.isLoading = false;
        // Handle push notification rejection
        if (err?.code === 'CONFIRMATION_REJECTED') {
          this.confirmationPopupError = err;
          this.isShowingInternalActionConfirmation = false;
          this.isShowingConfirmationTab = false;
          // Reset to transaction form state
          this.confirmationId = null;
          // Show toast notification about rejection
          this.setTemporaryError({
            code: 'CONFIRMATION_REJECTED',
            message: i18n.getMessage ? i18n.getMessage('error.confirmationRejected') : 'Request was declined'
          });
        } else if (err?.code !== 'CONFIRMATION_PENDING_ACTION') {
          // Don't update error for CONFIRMATION_PENDING_ACTION - just continue polling
          this.confirmationPopupError = err;
        }
      });
    }
  }

  async resendPaymentSecurityCode() {
    try {
      runInAction(() => {
        this.isLoading = true;
      });
      const {
        confirmation_id: confirmationId,
        confirmation_type: confirmationType,
        expires_at: expiresAt
      } = await resendSecurityCode(BACKEND_MODULE_TYPES.PAYMENT, this.confirmationId);

      updateConfirmationActionType(confirmationType);
      setTimerDurationExpiresAt(expiresAt);

      runInAction(() => {
        this.confirmationPopupError = null;
        this.confirmationId = confirmationId;
        this.isShowingInternalActionConfirmation = true;
      });
    } catch (err) {
      runInAction(() => {
        this.confirmationPopupError = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async validateRecipientCryptoWalletAddress(customerNumber, data) {
    this.setIsLoading(true);
    try {
      const { paymentMethods } = await validateRecipientIbanOrCryptoWalletAddressRequest(customerNumber, data);

      const sortedPaymentMethods = sortingPaymentMethods(
        filteringPaymentMethods(paymentMethods, this.currentAccount?.transfer_provider),
        this.currentAccount?.currency,
        this.currentAccount?.transfer_provider === PAYMENT_PROVIDERS.MANUAL
          ? MANUAL_PROVIDER_PAYMENT_METHODS_SORTING_EXAMPLE
          : this.currentAccount?.transfer_provider === PAYMENT_PROVIDERS.GRAPHEREX
            ? GRAPHEREX_PROVIDER_PAYMENT_METHODS_SORTING_EXAMPLE
            : this.currentAccount?.transfer_provider === PAYMENT_PROVIDERS.DUKASCOPY
              ? DUKASCOPY_PROVIDER_PAYMENT_METHODS_SORTING_EXAMPLE
              : this.currentAccount?.transfer_provider === PAYMENT_PROVIDERS.H_AND_H ||
                  this.currentAccount?.transfer_provider === PAYMENT_PROVIDERS.CONVERA ||
                  this.currentAccount?.transfer_provider === PAYMENT_PROVIDERS.CONVERA_MERCHANT ||
                  this.currentAccount?.transfer_provider === PAYMENT_PROVIDERS.CONVERA_API
                ? H_AND_H_PROVIDER_PAYMENT_METHODS_SORTING_EXAMPLE
                : AFTER_VALIDATE_PAYMENT_METHODS_SORTING_EXAMPLE,
        undefined,
        this.currentAccount?.transfer_provider
      );

      runInAction(() => {
        this.isInternalIban = true;

        if (this.isRepeatPayment) {
          this.availablePaymentMethods = moveSelectedMethodToFront(sortedPaymentMethods, this.paymentMethod);
          this.isRepeatPayment = false;
        } else {
          this.availablePaymentMethods = sortedPaymentMethods;
          this.paymentMethod = this.availablePaymentMethods[0];
        }
        if (paymentMethods.length === 0) {
          this.error = { fields: [{ field: 'recipientWallet', code: 'NO_PAYMENT_METHODS_AVAILABLE' }] };
        }
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async checkCryptoTransfer(customerNumber, data) {
    this.setIsLoading(true);
    try {
      const transactionData = await transactionCryptoCheck(customerNumber, data);

      runInAction(() => {
        if (!this.isRepeatPayment) {
          this.paymentMethod = transactionData.payment_method;
        }

        if (this.isRepeatPayment) {
          this.isRepeatPayment = false;
        }
        this.serverTransactionData = { ...this.serverTransactionData, amountDetails: transactionData?.amountDetails };
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        if (err.code === 'WALLET_NOT_FOUND') {
          this.error = { code: err.code, message: i18n.getMessage('error.WALLET_NOT_FOUND') };
        }
        this.error = err;
        this.isLoading = false;
      });
    }
  }

  async validateCryptoTransfer({ customerNumber, providerData, model }) {
    this.setIsLoading(true);
    const data = model({
      providerTableData: providerData,
      data: {
        wallet: this.currentAccount,
        paymentMethod: this.paymentMethod,
        uploadedFiles: this.uploadedFiles.map((file) => file.id),
        ...this.ibanCredentials
      }
    });

    try {
      const transactionData = await transactionCryptoCheck(customerNumber, data);

      runInAction(() => {
        this.isLoading = false;
        if (!this.isPreparedTransactionsCreation) {
          this.isShowingConfirmationTab = true;
        }
        this.paymentMethod = transactionData.payment_method;
        this.transactionForCreate = {
          ...data,
          source_amount: transactionData.sourceAmountDetails?.amount || transactionData.source_amount,
          target_amount: transactionData.targetAmountDetails?.amount || transactionData.target_amount
        };
        this.serverTransactionData = transactionData;
        this.commission = {
          value: transactionData.totalCommissionDetails?.amount || transactionData.total_commissions,
          currency: transactionData.totalCommissionDetails?.currency || transactionData.currency_code,
          type: transactionData.commission_type,
          commissionDetails: transactionData.totalCommissionDetails
        };
      });
    } catch (err) {
      runInAction(() => {
        this.isLoading = false;
        this.error = err;
      });
    }
  }

  createCryptoTransfer(customerNumber) {
    return async () => {
      this.setIsLoading(true);
      try {
        const {
          confirmation_id: confirmationId,
          confirmation_type: confirmationType,
          expires_at: expiresAt
        } = await transactionCryptoCreate(customerNumber, {
          ...this.transactionForCreate
        });

        setTimerDurationExpiresAt(expiresAt);
        updateConfirmationActionType(confirmationType);

        runInAction(() => {
          this.isLoading = false;
          this.isShowingInternalActionConfirmation = true;
          this.confirmationId = confirmationId;
        });
      } catch (err) {
        runInAction(() => {
          this.isLoading = false;
          this.confirmationPopupError = err;
        });
      }
    };
  }

  async verifyCryptoSecurityCode(customerNumber, securityCode) {
    try {
      this.setIsLoading(true);
      await transactionCryptoConfirm(customerNumber, {
        code: securityCode,
        confirmation_id: this.confirmationId
      });

      runInAction(() => {
        this.isLoading = false;
        this.transactionNumber = null;
        this.isShowingConfirmationTab = false;
        this.isShowingInternalActionConfirmation = false;
        this.isSuccess = true;
        this.uploadedFiles = [];
        this.previousTransactionInfo = null;
        this.ibanCredentials = null;
        this.iban = null;
        this.previousIban = null;
        this.providerData = {};
      });
    } catch (err) {
      runInAction(() => {
        this.isLoading = false;
        // Handle push notification rejection
        if (err?.code === 'CONFIRMATION_REJECTED') {
          this.confirmationPopupError = err;
          this.isShowingInternalActionConfirmation = false;
          this.isShowingConfirmationTab = false;
          // Reset to transaction form state
          this.confirmationId = null;
          // Show toast notification about rejection
          this.setTemporaryError({
            code: 'CONFIRMATION_REJECTED',
            message: i18n.getMessage ? i18n.getMessage('error.confirmationRejected') : 'Request was declined'
          });
        } else if (err?.code !== 'CONFIRMATION_PENDING_ACTION') {
          // Don't update error for CONFIRMATION_PENDING_ACTION - just continue polling
          this.confirmationPopupError = err;
        }
      });
    }
  }

  setIsPreparedTransactionsLoading(status) {
    this.isPreparedTransactionsLoading = status;
    this.preparedTransactionsError = null;
  }

  setIsPreparedTransactionsCreation(status) {
    this.isPreparedTransactionsCreation = status;
    this.preparedTransactionsError = null;
  }

  setIsPreparedTransactionsSuccessfullyCreated(status) {
    this.isPreparedTransactionsSuccessfullyCreated = status;
    this.preparedTransactionsError = null;
  }
  setIsPreparedTransactionsSuccessfullyDeleted(status) {
    this.isPreparedTransactionsSuccessfullyDeleted = status;
    this.preparedTransactionsError = null;
  }
  setIsPreparedTransactionsSuccessfullyConfirmed(status) {
    this.isPreparedTransactionsSuccessfullyConfirmed = status;
    this.preparedTransactionsError = null;
  }
  setConfirmationId(confirmationId) {
    this.confirmationId = confirmationId;
  }

  setPreparedTransactionsFilter(fieldName, value) {
    this.preparedTransactionsFilters[fieldName] = Array.isArray(value) && value.length === 0 ? null : value;
    this.preparedTransactionsPagination.page = 0;
  }

  setPreparedTransactionsPage(page) {
    this.preparedTransactionsPagination.page = page;
  }

  setPreparedTransactionsPageSize(size) {
    this.preparedTransactionsPagination.size = size;
  }

  resetPreparedTransactionData() {
    this.previousTransactionInfo = null;
    this.iban = null;
    this.isRepeatPayment = false;
    this.paymentMethod = null;
    this.availablePaymentMethods = [];
    this.isPreparedTransactionsCreation = false;
    this.isPreparedTransactionsSuccessfullyCreated = false;
    this.isPreparedTransactionsSuccessfullyDeleted = false;
    this.isPreparedTransactionsSuccessfullyConfirmed = false;
    this.preparedTransactionIdForEdit = null;
    this.preparedTransactionDataForUpdate = null;
    this.preparedTransactionsError = null;
    this.confirmationPopupError = null;
    this.uploadedFiles = [];
    this.isShowingInternalActionConfirmation = false;
    this.isShowSwitchConfirmationMethodScreen = false;
  }

  async createPreparedTransaction(customerNumber, isCrypto = false) {
    try {
      this.setIsLoading(true);

      const data = {
        ...this.transactionForCreate
      };

      if (isCrypto) {
        await createPreparedCryptoTransaction(customerNumber, data);
      } else {
        await createPreparedTransaction(customerNumber, data);
      }

      runInAction(() => {
        this.isPreparedTransactionsSuccessfullyCreated = true;
        this.isPreparedTransactionsCreation = false;
        this.preparedTransactionsError = null;
      });
    } catch (err) {
      runInAction(() => {
        this.preparedTransactionsError = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async updatePreparedTransaction(customerNumber, isCrypto = false) {
    try {
      this.setIsPreparedTransactionsLoading(true);

      // Add source_amount and target_amount from preparedTransactionDataForUpdate
      const data = {
        ...this.transactionForCreate,
        source_amount: this.preparedTransactionDataForUpdate?.source_amount || this.transactionForCreate?.source_amount,
        target_amount: this.preparedTransactionDataForUpdate?.target_amount || this.transactionForCreate?.target_amount
      };

      if (isCrypto) {
        await updatePreparedCryptoTransaction(customerNumber, this.preparedTransactionIdForEdit, data);
      } else {
        await updatePreparedTransactionRequest(customerNumber, this.preparedTransactionIdForEdit, data);
      }

      runInAction(() => {
        this.isPreparedTransactionsSuccessfullyCreated = true;
        this.isPreparedTransactionsCreation = false;
        this.preparedTransactionsError = null;
      });
    } catch (err) {
      runInAction(() => {
        this.preparedTransactionsError = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async deletePreparedTransactions(customerNumber, preparedTransactionIds) {
    try {
      this.setIsPreparedTransactionsLoading(true);

      await deletePreparedTransactions(customerNumber, preparedTransactionIds);

      runInAction(() => {
        this.isPreparedTransactionsSuccessfullyDeleted = true;
        this.isPreparedTransactionsLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.preparedTransactionsError = err;
        this.isPreparedTransactionsLoading = false;
      });
      throw err;
    }
  }

  async submitPreparedTransactions(customerNumber, preparedTransactionIds) {
    try {
      this.setIsPreparedTransactionsLoading(true);
      const {
        confirmation_id: confirmationId,
        confirmation_type: confirmationType,
        expires_at: expiresAt
      } = await submitPreparedTransactions(customerNumber, preparedTransactionIds);

      setTimerDurationExpiresAt(expiresAt);
      updateConfirmationActionType(confirmationType);

      runInAction(() => {
        this.confirmationId = confirmationId;
        this.preparedTransactionsError = false;
        this.isPreparedTransactionsLoading = false;
        this.isShowingInternalActionConfirmation = true;
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('submit prepared transactions error', err);
      runInAction(() => {
        this.preparedTransactionsError = err;
        this.isPreparedTransactionsLoading = false;
      });
    }
  }

  async confirmPreparedTransactions(customerNumber, code) {
    try {
      this.setIsPreparedTransactionsLoading(true);

      await confirmPreparedTransactions(customerNumber, {
        confirmation_id: this.confirmationId,
        code
      });

      runInAction(() => {
        this.confirmationId = null;
        this.isPreparedTransactionsSuccessfullyConfirmed = true;
        this.preparedTransactionsError = false;
        this.isPreparedTransactionsLoading = false;
        this.isShowingInternalActionConfirmation = false;
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('confirm prepared transactions error', err);
      runInAction(() => {
        // Handle push notification rejection
        if (err?.code === 'CONFIRMATION_REJECTED') {
          this.confirmationPopupError = err;
          this.isShowingInternalActionConfirmation = false;
          // Reset to form state
          this.confirmationId = null;
          // Show toast notification about rejection
          this.setTemporaryError({
            code: 'CONFIRMATION_REJECTED',
            message: i18n.getMessage ? i18n.getMessage('error.confirmationRejected') : 'Request was declined'
          });
        } else if (err?.code !== 'CONFIRMATION_PENDING_ACTION') {
          this.preparedTransactionsError = err;
          this.confirmationPopupError = err;
        }
        this.isPreparedTransactionsLoading = false;
      });
    }
  }

  getPreparedTransactionErrorById(preparedTransactionId) {
    if (this.preparedTransactionsError instanceof PreparedTransactionsError) {
      return this.preparedTransactionsError.getErrorByPreparedTransactionId(preparedTransactionId);
    }
    return undefined;
  }

  isErrorForPreparedTransaction(preparedTransactionId) {
    return !!this.getPreparedTransactionErrorById(preparedTransactionId);
  }

  getPreparedTransactionsWithDebounce = debounce(this.getPreparedTransactions, 400);

  async getPreparedTransactions(customerNumber) {
    try {
      this.setIsPreparedTransactionsLoading(true);
      const query = {
        search_text: this.preparedTransactionsFilters.search_text,
        from_date: convertDateToServerFilter(this.preparedTransactionsFilters.from_date),
        to_date: convertDateToServerFilter(this.preparedTransactionsFilters.to_date),
        currency: this.preparedTransactionsFilters.currency,
        wallet_number: this.preparedTransactionsFilters.wallet_number,
        page: this.preparedTransactionsPagination.page,
        size: this.preparedTransactionsPagination.size
      };

      const { content, total_elements, total_pages, number, size } = await getPreparedTransactionsList(
        customerNumber,
        query
      );

      const parsedContent = content.map((transaction) => {
        try {
          const parsedTransactionData = JSON.parse(transaction.transactionData);
          return {
            ...transaction,
            transactionData: parsedTransactionData
          };
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(`Error parsing transactionData for transaction ${transaction.id}:`, error);
          return {
            ...transaction,
            transactionData: {}
          };
        }
      });

      runInAction(() => {
        this.preparedTransactions = parsedContent;
        this.preparedTransactionsTotalElements = total_elements;
        this.preparedTransactionsTotalPages = total_pages;
        this.preparedTransactionsPagination.size = size;
        this.preparedTransactionsPagination.page = number;
        this.isPreparedTransactionsLoading = false;
        this.isPreparedTransactionsFetched = true;
      });
    } catch (err) {
      runInAction(() => {
        this.preparedTransactionsError = err;
        this.isPreparedTransactionsLoading = false;
      });
      throw err;
    }
  }

  async getPreparedTransactionCommission(customerNumber, data, preparedTransactionId, setSelectedTransaction) {
    this.setIsCommissionLoading(true);
    try {
      const { commission, commissionDetails } = await calculatePaymentCommissionRequest(customerNumber, data);

      const updatedPreparedTransactions = this.preparedTransactions.map((transaction) => {
        if (transaction.id === preparedTransactionId) {
          const updatedTransaction = {
            ...transaction,
            commission: commissionDetails?.amount || commission,
            commissionDetails
          };

          setSelectedTransaction([updatedTransaction]);

          return updatedTransaction;
        } else {
          return transaction;
        }
      });

      runInAction(() => {
        this.preparedTransactions = updatedPreparedTransactions;
        this.isCommissionLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
        this.isCommissionLoading = false;
      });
    }
  }

  async getPreparedTransactionsCommissionsInBatch(customerNumber, data, preparedTransactions, setSelectedTransaction) {
    this.setIsPreparedTransactionsLoading(true);
    try {
      const commissions = await calculatePaymentCommissionInBatchRequest(customerNumber, data);

      const updatedPreparedTransactions = preparedTransactions.map((transaction, index) => {
        return {
          ...transaction,
          commission: commissions[index]?.commissionDetails?.amount || commissions[index]?.commission,
          commissionDetails: commissions[index]?.commissionDetails
        };
      });

      setSelectedTransaction(updatedPreparedTransactions);

      runInAction(() => {
        this.isPreparedTransactionsLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
        this.isPreparedTransactionsLoading = false;
      });
    }
  }

  async getPreparedTransactionDataForEdit(customerNumber, preparedTransactionId, userAccounts) {
    try {
      this.setIsPreparedTransactionsLoading(true);
      const transactionData = await getPreparedTransactionDataForEditRequest(customerNumber, preparedTransactionId);

      if (!transactionData.purpose) {
        const preparedTransaction = this.preparedTransactions.find((t) => t.id === preparedTransactionId);
        if (preparedTransaction?.transactionData?.purpose) {
          transactionData.purpose = preparedTransaction.transactionData.purpose;
        }
      }

      const senderAccount = userAccounts.find(
        (userAccount) =>
          userAccount.wallet_number === transactionData?.wallet_number ||
          userAccount.wallet_number === transactionData?.sender_wallet_number
      );

      this.setSelectedAccount(senderAccount);
      this.setPreviousTransactionInfo(transactionData, true);
      this.setIsRepeatPaymentStatus(true);
      this.setPaymentMethod(
        senderAccount?.transfer_provider === PAYMENT_PROVIDERS.GRAPHEREX
          ? PAYMENT_METHOD.CRYPTO
          : transactionData.payment_method
      );
      this.setAvailablePaymentMethods(
        filteringPaymentMethods(senderAccount?.payment_methods, senderAccount?.transfer_provider)
      );

      runInAction(() => {
        this.preparedTransactionIdForEdit = preparedTransactionId;
        this.preparedTransactionsError = null;
        this.isPreparedTransactionsLoading = false;
        this.preparedTransactionDataForUpdate = transactionData;
      });
    } catch (err) {
      runInAction(() => {
        this.preparedTransactionsError = err;
        this.isPreparedTransactionsLoading = false;
      });
      throw err;
    }
  }

  setCurrentTemplateStep(step) {
    this.currentTemplateStep = step;
  }

  setTemplateData(data) {
    this.templateData = { ...this.templateData, ...data };
  }

  setIsTemplateSuccessfullyCreated(status) {
    this.isTemplateSuccessfullyCreated = status;
  }

  setIsTemplateSuccessfullyUpdated(status) {
    this.isTemplateSuccessfullyUpdated = status;
  }

  setIsTemplateSuccessfullyDeleted(status) {
    this.isTemplateSuccessfullyDeleted = status;
  }

  setIsTemplateTransaction(status) {
    this.isTemplateTransaction = status;
  }

  setEditTemplateId(templateId) {
    this.editTemplateId = templateId;
    this.isTemplateUpdating = templateId !== null;
  }

  setTemplatesFilter(fieldName, value) {
    this.templatesFilters[fieldName] = Array.isArray(value) && value.length === 0 ? null : value;
    this.templatesPagination.page = 0;
  }

  resetTemplateCreationData() {
    this.templateData = {};
    this.templateSteps = TEMPLATE_CREATION_STEPS_FOR_OTHER_PAYMENT_METHODS;
    this.currentTemplateStep = TEMPLATE_CREATION_STEPS.ACCOUNT_DETAILS;
    this.isTemplateSuccessfullyCreated = false;
    this.isTemplateSuccessfullyUpdated = false;
    this.isTemplateUpdating = false;
  }

  /* ██████████████████████████████ *\
   * █████ CSV-IMPORT-ACTIONS █████ *
  \* ██████████████████████████████ */

  resetCsvImportState() {
    this.isCsvImporting = false;
    this.csvImportError = null;
    this.csvImportValidationErrors = null;
  }

  async importCsv(customerNumber, file) {
    this.isCsvImporting = true;
    this.csvImportError = null;
    this.csvImportValidationErrors = null;

    try {
      const formData = new FormData();
      formData.append('file', file, file.name);

      await importCsvTransactions(customerNumber, formData);

      runInAction(() => {
        this.isCsvImporting = false;
      });

      return { success: true };
    } catch (err) {
      runInAction(() => {
        this.isCsvImporting = false;

        if (err.code === 'ARGUMENT_NOT_VALID' && Array.isArray(err.details) && err.details.length > 0) {
          this.csvImportValidationErrors = err.details;
        } else {
          this.csvImportError = err;
        }
      });

      return { success: false };
    }
  }

  getTemplatesListWithDebounce = debounce(this.getPreparedTransactions, 400);

  async getTemplatesList(customerNumber) {
    try {
      this.setIsLoading(true);

      const query = {
        search_text: this.templatesFilters.search_text,
        currency: this.templatesFilters.currency,
        page: this.templatesPagination.page,
        size: this.templatesPagination.size
      };

      const { content, total_elements, total_pages, number, size } = await getTemplatesListRequest(
        customerNumber,
        query
      );

      runInAction(() => {
        this.templatesList = content;
        this.templatesTotalElements = total_elements;
        this.templatesTotalPages = total_pages;
        this.templatesPagination.size = size;
        this.templatesPagination.page = number;
        this.isTemplatesListFetched = true;
        this.isTemplateReadyToUse = false;
      });
    } catch (err) {
      runInAction(() => {
        this.preparedTransactionsError = err;
        this.isPreparedTransactionsLoading = false;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async validateTemplateAccountDetails(customerNumber, data, isCryptoPayment = false) {
    try {
      this.setIsLoading(true);

      const response = await validateTemplateAccountDetailsRequest(customerNumber, data);

      const currentTemplateData = { ...this.templateData };

      if (response.paymentMethod === PAYMENT_METHOD.INTERNAL) {
        if (response.network) {
          response.cryptoNetwork = response.network;
          response.cryptoAddress = response.accountNumber;
          response.accountNumber = response.internalWalletNumber;
        } else {
          if (response?.accountNumber?.length > 8) {
            response.iban = response.accountNumber;
          }
          response.accountNumber = response.internalWalletNumber;
        }
      }

      const mergedData = {
        ...currentTemplateData,
        ...Object.fromEntries(
          Object.entries(response).map(([key, value]) => [key, value === null ? currentTemplateData[key] : value])
        )
      };

      runInAction(() => {
        this.templateData = mergedData;
        this.templateSteps =
          response.paymentMethod === PAYMENT_METHOD.SWIFT || response.paymentMethod === PAYMENT_METHOD.TARGET2
            ? TEMPLATE_CREATION_STEPS_FOR_SWIFT
            : isCryptoPayment
              ? TEMPLATE_CREATION_STEPS_FOR_CRYPTO_PAYMENT
              : TEMPLATE_CREATION_STEPS_FOR_OTHER_PAYMENT_METHODS;
      });

      this.goToNextStep();
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async validateTemplateBankDetails(customerNumber, data) {
    try {
      this.setIsLoading(true);

      const response = await validateTemplateBankDetailsRequest(customerNumber, data);

      runInAction(() => {
        this.templateData = { ...this.templateData, ...response };
      });

      this.goToNextStep();
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async validateTemplateRecipientDetails(customerNumber, data) {
    try {
      this.setIsLoading(true);

      const response = await validateTemplateRecipientDetailsRequest(customerNumber, data);

      runInAction(() => {
        this.templateData = { ...this.templateData, ...response };
      });

      this.goToNextStep();
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async validateTemplateAdditionalRecipientDetails(customerNumber, data) {
    try {
      this.setIsLoading(true);

      const response = await validateTemplateAdditionalRecipientDetailsRequest(customerNumber, data);

      runInAction(() => {
        this.templateData = { ...this.templateData, ...response };
      });

      this.goToNextStep();
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async validateTemplatePaymentDetails(customerNumber, data) {
    try {
      this.setIsLoading(true);

      const response = await validateTemplatePaymentDetailsRequest(customerNumber, data);

      runInAction(() => {
        this.templateData = { ...this.templateData, ...response };
      });

      this.goToNextStep();
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async createTemplate(customerNumber, data) {
    try {
      this.setIsLoading(true);

      await createTemplateRequest(customerNumber, data);

      runInAction(() => {
        this.isTemplateSuccessfullyCreated = true;
      });

      this.goToNextStep();
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async updateTemplate(customerNumber, data, templateId) {
    try {
      this.setIsLoading(true);

      await updateTemplateRequest(customerNumber, data, templateId);

      runInAction(() => {
        this.isTemplateSuccessfullyUpdated = true;
      });

      this.goToNextStep();
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async deleteTemplateRequest(customerNumber, templateId) {
    try {
      this.setIsLoading(true);

      await deleteTemplateRequest(customerNumber, templateId);

      runInAction(() => {
        this.isTemplateSuccessfullyDeleted = true;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async validateTemplatePaymentAmount(customerNumber, templateId, data) {
    try {
      this.setIsLoading(true);

      await validateTemplatePaymentAmountRequest(customerNumber, templateId, data);

      runInAction(() => {
        this.isTemplateReadyToUse = true;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  setTemplatesPage(page) {
    this.templatesPagination.page = page;
  }

  setTemplatesPageSize(size) {
    this.templatesPagination.size = size;
  }

  getCurrentStepIndex() {
    return this.templateSteps.indexOf(this.currentTemplateStep);
  }

  goToNextStep() {
    const idx = this.getCurrentStepIndex();
    if (idx < this.templateSteps.length - 1) {
      this.currentTemplateStep = this.templateSteps[idx + 1];
    }
  }

  goToPrevStep() {
    const idx = this.getCurrentStepIndex();
    if (idx > 0) {
      this.currentTemplateStep = this.templateSteps[idx - 1];
    }
  }

  forceSwitchToAnotherConfirmationType = async (confirmationType) => {
    try {
      runInAction(() => {
        this.isLoading = true;
      });

      const {
        confirmation_id: confirmationId,
        confirmation_type: type,
        expires_at: expiresAt
      } = await forceChangeConfirmationTypeRequest(this.confirmationId, confirmationType, BACKEND_MODULE_TYPES.PAYMENT);

      updateConfirmationActionType(type);
      setTimerDurationExpiresAt(expiresAt);

      runInAction(() => {
        this.confirmationId = confirmationId;
        this.confirmationPopupError = null;
      });
    } catch (err) {
      runInAction(() => {
        this.confirmationPopupError = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
        this.isShowSwitchConfirmationMethodScreen = false;
      });
    }
  };
}

export default new PaymentStore();
